import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthProvider';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import RichMarkdown from './RichMarkdown';

function AdminCMS() {
  const { user, isAdmin, loading } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [cvProjects, setCvProjects] = useState([]);
  const [texts, setTexts] = useState([]);
  const [view, setView] = useState('projects');
  const [homepageImages, setHomepageImages] = useState([]);
  const [homepageItems, setHomepageItems] = useState([]);
  const [bannerConfig, setBannerConfig] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState([[], []]);
  const [previewTitle, setPreviewTitle] = useState('');
  
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupScanning, setCleanupScanning] = useState(false);
  const [cleanupData, setCleanupData] = useState(null);
  const [cleanupDeleting, setCleanupDeleting] = useState(false);
  const [cleanupSelected, setCleanupSelected] = useState(new Set());

  // CV Projects drag & drop state
  const [cvProjectsOrder, setCvProjectsOrder] = useState([]);
  const [draggedCvIndex, setDraggedCvIndex] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  // Events drag & drop state
  const [eventsOrder, setEventsOrder] = useState([]);
  const [draggedEventIndex, setDraggedEventIndex] = useState(null);
  const [eventsOrderChanged, setEventsOrderChanged] = useState(false);
  const [savingEventsOrder, setSavingEventsOrder] = useState(false);

  const handleCleanupScan = async () => {
    setCleanupScanning(true);
    try {
      const res = await fetch('/api/cleanup-storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'scan' })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Scan failed');
      setCleanupData(json);
      setCleanupSelected(new Set(json.unused?.map((f, i) => i) || []));
    } catch (err) {
      alert('Scan failed: ' + err.message);
    } finally {
      setCleanupScanning(false);
    }
  };

  const handleCleanupDelete = async () => {
    if (cleanupSelected.size === 0) { alert('No files selected'); return; }
    if (!window.confirm(`Delete ${cleanupSelected.size} file(s)? This cannot be undone.`)) return;
    
    setCleanupDeleting(true);
    try {
      const filesToDelete = cleanupData.unused
        .filter((_, i) => cleanupSelected.has(i))
        .map(f => f.name);
      const res = await fetch('/api/cleanup-storage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', fileNames: filesToDelete })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      alert(json.message);
      setCleanupOpen(false);
      setCleanupData(null);
      setCleanupSelected(new Set());
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setCleanupDeleting(false);
    }
  };

  const safeParseContent = (content) => {
    if (content == null) return [[], []];
    if (Array.isArray(content)) return content;
    if (typeof content === 'string') {
      try { const parsed = JSON.parse(content); return Array.isArray(parsed) ? parsed : [[], []]; } catch (e) { return [[], []]; }
    }
    return [[], []];
  };

  const parseBannerConfig = useCallback((value) => {
    if (!value) {
      return {
        enabled: false,
        image_url: '',
        alt_text: 'Banner',
        link_type: 'none',
        link_url: '',
        link_project_id: null,
        max_width: 100,
        max_height: 300,
      };
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return {
          enabled: parsed.enabled ?? false,
          image_url: parsed.image_url ?? '',
          alt_text: parsed.alt_text ?? 'Banner',
          link_type: parsed.link_type ?? 'none',
          link_url: parsed.link_url ?? '',
          link_project_id: parsed.link_project_id ?? null,
          max_width: parsed.max_width ?? 100,
          max_height: parsed.max_height ?? 300,
        };
      } catch (e) {
        return parseBannerConfig(null);
      }
    }
    return parseBannerConfig(JSON.stringify(value));
  }, []);

  const parseHomepageItems = useCallback((value) => {
    if (!value) return [];
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, []);

  const saveHomepageItems = async (items) => {
    const row = {
      name: 'homepage_config',
      text: JSON.stringify(items),
    };
    const { data: existing } = await supabase.from('texts').select('name').eq('name', row.name).single();
    if (existing) {
      const { error: updateErr } = await supabase.from('texts').update(row).eq('name', row.name).select();
      if (updateErr) throw updateErr;
      return;
    }
    const { error: insertErr } = await supabase.from('texts').insert(row).select();
    if (insertErr) throw insertErr;
  };

  const fetchData = useCallback(async () => {
    setError(null);
    const [projRes, evRes, cvProjRes, homeRes, textsRes] = await Promise.all([
      supabase.from('projects').select('*').order('id'),
      supabase.from('events').select('*').order('order', { ascending: true, nullsFirst: false }).order('date'),
      supabase.from('cv-projects').select('*').order('order', { ascending: true, nullsFirst: false }).order('id'),
      supabase.from('homepage_images').select('*').order('id'),
      supabase.from('texts').select('*').order('name'),
    ]);
    const projErr = projRes.error; const evErr = evRes.error; const cvErr = cvProjRes.error; const homeErr = homeRes.error; const textsErr = textsRes.error;
    if (projErr || evErr || cvErr || homeErr || textsErr) setError(projErr?.message || evErr?.message || cvErr?.message || homeErr?.message || textsErr?.message);
    setProjects(projRes.data || []);
    const eventsWithOrder = (evRes.data || []).map((e, idx) => ({ ...e, order: e.order ?? idx }));
    setEvents(eventsWithOrder);
    setEventsOrder(eventsWithOrder);
    const cvProjectsWithOrder = (cvProjRes.data || []).map((p, idx) => ({ ...p, order: p.order ?? idx }));
    setCvProjects(cvProjectsWithOrder);
    setCvProjectsOrder(cvProjectsWithOrder);
    setHomepageImages(homeRes.data || []);
    setTexts(textsRes.data || []);
    const bannerRow = (textsRes.data || []).find((t) => t.name === 'banner_config');
    setBannerConfig(parseBannerConfig(bannerRow?.text));
    const homepageRow = (textsRes.data || []).find((t) => t.name === 'homepage_config');
    setHomepageItems(parseHomepageItems(homepageRow?.text));
    setOrderChanged(false);
    setEventsOrderChanged(false);
  }, [parseBannerConfig, parseHomepageItems]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  const handleAdd = () => {
    if (view === 'banner') {
      setEditingItem(bannerConfig ? { name: 'banner_config', text: JSON.stringify(bannerConfig) } : null);
      setModalOpen(true);
      return;
    }
    setEditingItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    if (view === 'banner') {
      setEditingItem({ name: 'banner_config', text: JSON.stringify(bannerConfig || {}) });
      setModalOpen(true);
      return;
    }
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      if (view === 'homepage-setup') {
        const isExisting = payload.id != null;
        const nextItem = {
          id: payload.id ?? `${Date.now()}`,
          title: payload.title ?? '',
          subtitle: payload.subtitle ?? '',
          preview_url: payload.preview_url ?? '',
          project_id: payload.project_id ?? null,
        };
        const nextItems = isExisting
          ? homepageItems.map((item) => String(item.id) === String(nextItem.id) ? nextItem : item)
          : [...homepageItems, nextItem];
        await saveHomepageItems(nextItems);
        setModalOpen(false);
        await fetchData();
        return;
      }

      const table = view === 'projects' ? 'projects' : view === 'events' ? 'events' : view === 'cv-projects' ? 'cv-projects' : view === 'page-texts' || view === 'banner' ? 'texts' : 'homepage_images';
      const row =
        table === 'projects'
          ? {
              id: payload.id ?? undefined,
              project_title: payload.project_title ?? '',
              description: payload.description ?? '',
              content: Array.isArray(payload.content) ? payload.content : [],
            }
          : table === 'events'
          ? {
              id: payload.id ?? undefined,
              title: payload.title ?? '',
              date: payload.date ?? null,
              venue: payload.venue ?? '',
              external_url: payload.external_url ?? null,
            }
          : table === 'cv-projects'
          ? {
              id: payload.id ?? undefined,
              title: payload.title ?? '',
              category: payload.category ?? '',
              project_id: payload.project_id ?? null,
              external_url: payload.external_url ?? null,
            }
          : view === 'banner'
          ? {
              name: 'banner_config',
              text: JSON.stringify(payload),
            }
          : table === 'texts'
          ? {
              name: payload.name ?? '',
              text: payload.text ?? '',
            }
          : {
              id: payload.id ?? undefined,
              url: payload.url ?? '',
              name: payload.name ?? null,
              is_primary: payload.is_primary ?? false,
              link_project_id: payload.link_project_id ?? null,
              link_url: payload.link_url ?? null,
            };

      if (table === 'projects') {
        let projectId = payload.id;
        if (!projectId) {
          const insertRow = { ...row };
          const { data: inserted, error: insertErr } = await supabase.from('projects').insert(insertRow).select().single();
          if (insertErr) throw insertErr;
          projectId = inserted.id;
        } else {
          const updateRow = { ...row };
          const { error: updateErr } = await supabase.from('projects').update(updateRow).eq('id', projectId);
          if (updateErr) throw updateErr;
        }

        const pending = Array.isArray(payload.pendingFiles) ? payload.pendingFiles : [];
        const uploadedMap = {};
        if (pending.length > 0) {
          for (const pf of pending) {
            const form = new FormData();
            const file = pf && pf.file ? pf.file : pf;
            const pendingId = pf && pf.id ? pf.id : null;
            form.append('file', file);
            form.append('projectId', String(projectId));
            const headers = {};
            if (process.env.REACT_APP_UPLOAD_API_KEY) headers['x-api-key'] = process.env.REACT_APP_UPLOAD_API_KEY;
            const resp = await fetch('/api/upload-image', { method: 'POST', body: form, headers });
            const json = await resp.json();
            if (!resp.ok) throw new Error(json.error || 'Upload failed');
            if (json.url && pendingId) uploadedMap[pendingId] = json.url;
          }
        }

        const existingContent = Array.isArray(row.content) ? row.content : [[], []];
        const finalContent = existingContent.map((col) => col.map((block) => {
          if (block && block.__pendingId && uploadedMap[block.__pendingId]) {
            return { ...block, value: uploadedMap[block.__pendingId] };
          }
          return block;
        }));

        const finalUpdate = { ...row, content: finalContent };
        delete finalUpdate.id;
        const { error: finalErr } = await supabase.from('projects').update(finalUpdate).eq('id', projectId).select();
        if (finalErr) throw finalErr;
      } else if (table === 'texts') {
        // For texts table, check if record exists and update or insert accordingly
        const { data: existing } = await supabase.from('texts').select('name').eq('name', row.name).single();
        
        if (existing) {
          // Update existing record
          const { error: updateErr } = await supabase.from('texts').update(row).eq('name', row.name).select();
          if (updateErr) throw updateErr;
        } else {
          // Insert new record
          const { error: insertErr } = await supabase.from('texts').insert(row).select();
          if (insertErr) throw insertErr;
        }
      } else {
        const { error: upsertErr } = await supabase.from(table).upsert(row).select();
        if (upsertErr) throw upsertErr;
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setError(null);
    if (view === 'homepage-setup') {
      try {
        const nextItems = homepageItems.filter((item) => String(item.id) !== String(id));
        await saveHomepageItems(nextItems);
        await fetchData();
      } catch (e) {
        setError(e.message || String(e));
      }
      return;
    }
    const table = view === 'projects' ? 'projects' : view === 'events' ? 'events' : view === 'cv-projects' ? 'cv-projects' : view === 'page-texts' || view === 'banner' ? 'texts' : 'homepage_images';
    const deleteKey = view === 'page-texts' || view === 'banner' ? 'name' : 'id';
    const { error: delErr } = await supabase.from(table).delete().eq(deleteKey, id);
    if (delErr) setError(delErr.message);
    await fetchData();
  };

  const handleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/admin` }
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  // CV Projects drag & drop handlers
  const handleCvDragStart = (index) => {
    setDraggedCvIndex(index);
  };

  const handleCvDragOver = (e) => {
    e.preventDefault();
  };

  const handleCvDrop = (dropIndex) => {
    if (draggedCvIndex === null || draggedCvIndex === dropIndex) {
      setDraggedCvIndex(null);
      return;
    }

    const reordered = [...cvProjectsOrder];
    const [draggedItem] = reordered.splice(draggedCvIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    setCvProjectsOrder(reordered);
    setOrderChanged(true);
    setDraggedCvIndex(null);
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    setError(null);
    try {
      // Update order for all cv-projects
      const updates = cvProjectsOrder.map((item, idx) => ({
        id: item.id,
        order: idx
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('cv-projects')
          .update({ order: update.order })
          .eq('id', update.id);
        
        if (error) throw error;
      }

      await fetchData();
      alert('Order saved successfully!');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSavingOrder(false);
    }
  };

  // Events drag & drop handlers
  const handleEventDragStart = (index) => {
    setDraggedEventIndex(index);
  };

  const handleEventDragOver = (e) => {
    e.preventDefault();
  };

  const handleEventDrop = (dropIndex) => {
    if (draggedEventIndex === null || draggedEventIndex === dropIndex) {
      setDraggedEventIndex(null);
      return;
    }

    const reordered = [...eventsOrder];
    const [draggedItem] = reordered.splice(draggedEventIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    setEventsOrder(reordered);
    setEventsOrderChanged(true);
    setDraggedEventIndex(null);
  };

  const handleSaveEventsOrder = async () => {
    setSavingEventsOrder(true);
    setError(null);
    try {
      const updates = eventsOrder.map((item, idx) => ({
        id: item.id,
        order: idx
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('events')
          .update({ order: update.order })
          .eq('id', update.id);
        
        if (error) throw error;
      }

      await fetchData();
      alert('Events order saved successfully!');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSavingEventsOrder(false);
    }
  };

  const handleMoveHomepageItem = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= homepageItems.length) return;
    setSaving(true);
    setError(null);
    try {
      const nextItems = [...homepageItems];
      const [item] = nextItems.splice(index, 1);
      nextItems.splice(target, 0, item);
      await saveHomepageItems(nextItems);
      await fetchData();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          padding: '48px 40px',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          maxWidth: 420,
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{
              margin: '0 0 8px 0',
              fontSize: 24,
              fontWeight: 600,
              color: '#1f2937'
            }}>
              Admin Login
            </h2>
            <p style={{
              margin: 0,
              fontSize: 14,
              color: '#6b7280',
              lineHeight: 1.5
            }}>
              Sign in with your Google account to access the admin panel
            </p>
          </div>
          
          <button
            onClick={handleLogin}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 15,
              transition: 'background 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10
            }}
            onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
            onMouseLeave={(e) => e.target.style.background = '#2563eb'}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.909-2.258c-.806.54-1.837.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#ffffff',
          padding: '48px 40px',
          borderRadius: 8,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          maxWidth: 420,
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: 64,
            height: 64,
            margin: '0 auto 24px',
            background: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32
          }}>
            🔒
          </div>
          <h2 style={{
            margin: '0 0 12px 0',
            fontSize: 24,
            fontWeight: 600,
            color: '#1f2937'
          }}>
            Unauthorized Access
          </h2>
          <p style={{
            margin: '0 0 32px 0',
            fontSize: 14,
            color: '#6b7280',
            lineHeight: 1.6
          }}>
            Your email is not authorized to access the admin panel. Please contact the administrator if you believe this is an error.
          </p>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 15,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#dc2626'}
            onMouseLeave={(e) => e.target.style.background = '#ef4444'}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const currentItems = view === 'projects' ? projects : view === 'events' ? events : view === 'cv-projects' ? cvProjects : view === 'page-texts' ? texts : view === 'banner' ? [bannerConfig].filter(Boolean) : view === 'homepage-setup' ? homepageItems : homepageImages;
  const scrollAreaStyle = {
    maxHeight: 'calc(100vh - 240px)',
    overflowY: 'auto',
    overflowX: 'auto',
  };

  function renderTable(items, onEdit, onDelete) {
    if (view === 'cv-projects') {
      return (
        <div>
          {orderChanged && (
            <div style={{ marginBottom: 12, padding: 12, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6 }}>
              <strong>Order changed!</strong> Click "Save Project Order" to save changes.
            </div>
          )}
          <button 
            onClick={handleSaveOrder} 
            disabled={!orderChanged || savingOrder}
            style={{
              padding: '8px 16px',
              marginBottom: 12,
              background: orderChanged ? '#28a745' : '#ccc',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: orderChanged ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            {savingOrder ? 'Saving...' : 'Save Project Order'}
          </button>
          <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
            <div style={scrollAreaStyle}>
              {cvProjectsOrder.map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleCvDragStart(index)}
                  onDragOver={handleCvDragOver}
                  onDrop={() => handleCvDrop(index)}
                  style={{
                    padding: 16,
                    borderBottom: index < cvProjectsOrder.length - 1 ? '1px solid #eee' : 'none',
                    background: draggedCvIndex === index ? '#f0f0f0' : '#fff',
                    cursor: 'grab',
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 150px 120px',
                    gap: 12,
                    alignItems: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = draggedCvIndex === index ? '#f0f0f0' : '#fff'}
                >
                  <div style={{ fontSize: 20, color: '#999', textAlign: 'center' }}>☰</div>
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      Category: {item.category}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {item.external_url ? (
                      <span>External: {item.external_url.substring(0, 20)}...</span>
                    ) : item.project_id ? (
                      <span>Project: {item.project_id}</span>
                    ) : (
                      <span style={{ color: '#ccc' }}>No link</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => onEdit(item)} style={{ padding: '6px 12px', fontSize: 13 }}>Edit</button>
                    <button onClick={() => onDelete(item.id)} style={{ padding: '6px 12px', fontSize: 13, background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (view === 'events') {
      return (
        <div>
          <button 
            onClick={handleSaveEventsOrder} 
            disabled={!eventsOrderChanged || savingEventsOrder}
            style={{
              padding: '8px 16px',
              marginBottom: 12,
              background: eventsOrderChanged ? '#28a745' : '#ccc',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: eventsOrderChanged ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            {savingEventsOrder ? 'Saving...' : 'Save Events Order'}
          </button>
          <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
            <div style={scrollAreaStyle}>
              {eventsOrder.map((item, index) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleEventDragStart(index)}
                  onDragOver={handleEventDragOver}
                  onDrop={() => handleEventDrop(index)}
                  style={{
                    padding: 16,
                    borderBottom: index < eventsOrder.length - 1 ? '1px solid #eee' : 'none',
                    background: draggedEventIndex === index ? '#f0f0f0' : '#fff',
                    cursor: 'grab',
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 120px 1fr 120px',
                    gap: 12,
                    alignItems: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = draggedEventIndex === index ? '#f0f0f0' : '#fff'}
                >
                  <div style={{ fontSize: 20, color: '#999', textAlign: 'center' }}>☰</div>
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {item.venue}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {item.date || 'No date'}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.external_url ? (
                      <a href={item.external_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
                        {item.external_url}
                      </a>
                    ) : (
                      <span style={{ color: '#ccc' }}>No link</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => onEdit(item)} style={{ padding: '6px 12px', fontSize: 13 }}>Edit</button>
                    <button onClick={() => onDelete(item.id)} style={{ padding: '6px 12px', fontSize: 13, background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (view === 'projects') {
      return (
        <div style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={scrollAreaStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Title</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Content</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#1f2937', fontWeight: 500 }}>{item.project_title}</td>
                    <td style={{ fontSize: 13, padding: '12px 16px' }}>
                      {(() => {
                        const content = safeParseContent(item.content);
                        const left = Array.isArray(content[0]) ? content[0].length : 0;
                        const right = Array.isArray(content[1]) ? content[1].length : 0;
                        if (left + right === 0) return (<span style={{ color: '#d1d5db' }}>—</span>);
                        return (
                          <button 
                            onClick={() => { setPreviewTitle(item.project_title || item.title || `Item ${item.id}`); setPreviewContent(content); setPreviewOpen(true); }}
                            style={{
                              padding: '4px 8px',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                          >
                            {left} | {right}
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button 
                          onClick={() => onEdit(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
                          onMouseLeave={(e) => e.target.style.background = '#2563eb'}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => onDelete(item.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (view === 'page-texts') {
      return (
        <div style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={scrollAreaStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Text Preview</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.name} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#1f2937', fontWeight: 500 }}>{item.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button 
                          onClick={() => onEdit(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
                          onMouseLeave={(e) => e.target.style.background = '#2563eb'}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => onDelete(item.name)}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (view === 'homepage-setup') {
      return (
        <div style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={scrollAreaStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Preview</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Label</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Project</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Order</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const linkedProject = projects.find((project) => String(project.id) === String(item.project_id));
                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}>
                      <td style={{ padding: '12px 16px' }}>{item.preview_url ? <img src={item.preview_url} alt={item.title || 'preview'} style={{ maxHeight: 60, borderRadius: 4 }} /> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                      <td style={{ padding: '12px 16px', fontSize: 14, color: '#1f2937' }}>
                        <div style={{ fontWeight: 500 }}>{item.title || <span style={{ color: '#d1d5db' }}>Untitled</span>}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{item.subtitle || 'No subtitle'}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{linkedProject?.project_title || <span style={{ color: '#d1d5db' }}>No project selected</span>}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleMoveHomepageItem(index, -1)} disabled={index === 0 || saving} style={{ padding: '6px 10px', fontSize: 13 }}>Up</button>
                          <button onClick={() => handleMoveHomepageItem(index, 1)} disabled={index === items.length - 1 || saving} style={{ padding: '6px 10px', fontSize: 13 }}>Down</button>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => onEdit(item)} style={{ padding: '6px 12px', fontSize: 13, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                          <button onClick={() => onDelete(item.id)} style={{ padding: '6px 12px', fontSize: 13, background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (view === 'homepage-images') {
      return (
        <div style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <div style={scrollAreaStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Preview</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Description</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 13, color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}>
                    <td style={{ padding: '12px 16px' }}>{item.url ? <img src={item.url} alt={item.name || 'image'} style={{ maxHeight: 60, borderRadius: 4 }} /> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, color: '#6b7280', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button 
                          onClick={() => onEdit(item)}
                          style={{
                            padding: '6px 12px',
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#1d4ed8'}
                          onMouseLeave={(e) => e.target.style.background = '#2563eb'}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => onDelete(item.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 500,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (view === 'banner') {
      const config = bannerConfig || {
        enabled: false,
        image_url: '',
        alt_text: 'Banner',
        link_type: 'none',
        link_url: '',
        link_project_id: null,
        max_width: 100,
        max_height: 300,
      };
      return (
        <div style={{ display: 'grid', gap: 20 }}>
          <div style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Homepage Banner</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                  Control the home page banner image, visibility, click target, and display size.
                </div>
              </div>
              <button
                onClick={() => handleEdit(null)}
                style={{
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Edit Banner
              </button>
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 280, flex: 1 }}>
                <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>Status</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: config.enabled ? '#10b981' : '#ef4444' }} />
                  <span style={{ fontSize: 13, color: '#374151' }}>{config.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div style={{ marginTop: 18, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>Link Target</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                  {config.link_type === 'internal' && config.link_project_id ? `Project ID ${config.link_project_id}` : config.link_type === 'external' && config.link_url ? config.link_url : 'No link configured'}
                </div>
              </div>
              <div style={{ minWidth: 280, flex: 1 }}>
                <div style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#1f2937' }}>Preview</div>
                {config.image_url ? (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#f9fafb' }}>
                    <img
                      src={config.image_url}
                      alt={config.alt_text || 'Banner'}
                      style={{
                        maxWidth: '100%',
                        width: config.max_width ? `${config.max_width}%` : '100%',
                        maxHeight: config.max_height ? `${config.max_height}px` : 'auto',
                        objectFit: 'contain',
                        borderRadius: 6,
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ padding: 18, border: '1px dashed #d1d5db', borderRadius: 8, color: '#9ca3af' }}>No banner image configured yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <AdminTable data={items} onEdit={onEdit} onDelete={onDelete} view={view} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, color: '#1f2937' }}>Admin CMS</h1>
            <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#6b7280' }}>Manage your portfolio content</p>
          </div>
          <button 
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              background: '#ef4444',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: 14,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#dc2626'}
            onMouseLeave={(e) => e.target.style.background = '#ef4444'}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Navigation & Controls */}
      <div style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <nav style={{ display: 'flex', gap: 4 }}>
            {['projects', 'events', 'cv-projects', 'page-texts', 'homepage-setup', 'homepage-images', 'banner'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                style={{
                  padding: '8px 16px',
                  background: view === v ? '#2563eb' : '#f3f4f6',
                  color: view === v ? '#fff' : '#6b7280',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: view === v ? 600 : 500,
                  fontSize: 14,
                  transition: 'all 0.2s',
                  textTransform: 'capitalize'
                }}
                onMouseEnter={(e) => {
                  if (view !== v) e.target.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  if (view !== v) e.target.style.background = '#f3f4f6';
                }}
              >
                {v === 'cv-projects' ? 'CV Projects' : v === 'page-texts' ? 'Page Texts' : v === 'homepage-setup' ? 'Homepage Setup' : v === 'banner' ? 'Banner' : v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </nav>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setCleanupOpen(true); handleCleanupScan(); }}
              style={{
                padding: '8px 16px',
                background: '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: 14,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#d97706'}
              onMouseLeave={(e) => e.target.style.background = '#f59e0b'}
            >
              🧹 Clean Storage
            </button>
            <button 
              onClick={handleAdd} 
              disabled={saving}
              style={{
                padding: '8px 16px',
                background: saving ? '#d1d5db' : '#10b981',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: 14,
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => { if (!saving) e.target.style.background = '#059669'; }}
              onMouseLeave={(e) => { if (!saving) e.target.style.background = '#10b981'; }}
            >
              {saving
                ? 'Saving…'
                : view === 'projects'
                ? '+ Add Project'
                : view === 'events'
                ? '+ Add Event'
                : view === 'cv-projects'
                ? '+ Add CV Project'
                : view === 'page-texts'
                ? '+ Add Text'
                : view === 'homepage-setup'
                ? '+ Add Homepage Item'
                : view === 'banner'
                ? 'Edit Banner'
                : '+ Add Homepage Image'}
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ padding: '24px' }}>
        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 14
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {renderTable(currentItems, handleEdit, handleDelete)}

        {modalOpen && (
          <AdminModal
            view={view}
            item={editingItem}
            saving={saving}
            onSave={handleSave}
            onClose={() => setModalOpen(false)}
            allProjects={projects}
          />
        )}

        {previewOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} onClick={() => setPreviewOpen(false)}>
            <div style={{ background: '#fff', padding: 24, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#1f2937' }}>{previewTitle}</h2>
                <button 
                  onClick={() => setPreviewOpen(false)}
                  style={{
                    padding: '6px 12px',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#6b7280',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; e.target.style.color = '#1f2937'; }}
                  onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#6b7280'; }}
                >
                  ✕ Close
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {previewContent.map((col, ci) => (
                  <div key={ci} style={{ border: '1px solid #e5e7eb', padding: 16, borderRadius: 8, background: '#f9fafb' }}>
                    {Array.isArray(col) && col.length > 0 ? col.map((block, bi) => (
                      <div key={bi} style={{ marginBottom: 16 }}>
                        {block.type === 'text' && <RichMarkdown source={block.value || ''} />}
                        {block.type === 'image' && <img src={block.value} alt={`preview-${ci}-${bi}`} style={{ maxWidth: '100%', borderRadius: 6 }} />}
                        {block.type === 'video' && <div style={{ fontSize: 13, color: '#6b7280' }}><a href={block.value} target="_blank" rel="noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>{block.value}</a></div>}
                      </div>
                    )) : (<div style={{ color: '#9ca3af', fontSize: 14 }}>No items</div>)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {cleanupOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} onClick={() => setCleanupOpen(false)}>
            <div style={{ background: '#fff', padding: 24, maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto', borderRadius: 8, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ margin: '0 0 20px 0', fontSize: 20, fontWeight: 600, color: '#1f2937' }}>🧹 Clean Storage</h2>
              {!cleanupData ? (
                <div>
                  <p style={{ color: '#6b7280', marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>Scan for unused images in storage that are not referenced by any project.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      onClick={handleCleanupScan} 
                      disabled={cleanupScanning}
                      style={{
                        padding: '8px 16px',
                        background: cleanupScanning ? '#d1d5db' : '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: cleanupScanning ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                        fontSize: 14,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => { if (!cleanupScanning) e.target.style.background = '#059669'; }}
                      onMouseLeave={(e) => { if (!cleanupScanning) e.target.style.background = '#10b981'; }}
                    >
                      {cleanupScanning ? 'Scanning...' : 'Start Scan'}
                    </button>
                    <button 
                      onClick={() => setCleanupOpen(false)}
                      style={{
                        padding: '8px 16px',
                        background: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 500,
                        fontSize: 14,
                        color: '#6b7280',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; }}
                      onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ fontSize: 14, color: '#166534', fontWeight: 500 }}>
                      <div>✓ <strong>Used:</strong> {cleanupData.usedCount} | <strong>Total:</strong> {cleanupData.storageCount} | <strong>Unused:</strong> {cleanupData.unusedCount}</div>
                    </div>
                  </div>
                  {cleanupData.unusedCount === 0 ? (
                    <p style={{ color: '#10b981', fontWeight: 500, marginBottom: 16 }}>✓ No unused images found!</p>
                  ) : (
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#1f2937' }}>Select files to delete:</p>
                      <div style={{ border: '1px solid #e5e7eb', maxHeight: '300px', overflowY: 'auto', marginBottom: 16, borderRadius: 6, background: '#f9fafb' }}>
                        {cleanupData.unused.map((file, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f3f4f6', fontSize: 14, cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}>
                            <input
                              type="checkbox"
                              checked={cleanupSelected.has(idx)}
                              onChange={(e) => {
                                const next = new Set(cleanupSelected);
                                if (e.target.checked) next.add(idx);
                                else next.delete(idx);
                                setCleanupSelected(next);
                              }}
                              style={{ marginRight: 10, cursor: 'pointer', width: 16, height: 16 }}
                            />
                            <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                              {file.name}
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>({(file.size / 1024).toFixed(1)} KB)</div>
                          </label>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button 
                          onClick={handleCleanupDelete} 
                          disabled={cleanupDeleting || cleanupSelected.size === 0}
                          style={{
                            padding: '8px 16px',
                            background: cleanupDeleting || cleanupSelected.size === 0 ? '#d1d5db' : '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: cleanupDeleting || cleanupSelected.size === 0 ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: 14,
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => { if (!(cleanupDeleting || cleanupSelected.size === 0)) e.target.style.background = '#dc2626'; }}
                          onMouseLeave={(e) => { if (!(cleanupDeleting || cleanupSelected.size === 0)) e.target.style.background = '#ef4444'; }}
                        >
                          {cleanupDeleting ? 'Deleting...' : `🗑️ Delete ${cleanupSelected.size} file(s)`}
                        </button>
                        <button 
                          onClick={() => { setCleanupData(null); handleCleanupScan(); }} 
                          disabled={cleanupDeleting}
                          style={{
                            padding: '8px 16px',
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            cursor: cleanupDeleting ? 'not-allowed' : 'pointer',
                            fontWeight: 500,
                            fontSize: 14,
                            color: '#6b7280',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { if (!cleanupDeleting) { e.target.style.background = '#e5e7eb'; } }}
                          onMouseLeave={(e) => { if (!cleanupDeleting) { e.target.style.background = '#f3f4f6'; } }}
                        >
                          ↻ Rescan
                        </button>
                        <button 
                          onClick={() => setCleanupOpen(false)} 
                          disabled={cleanupDeleting}
                          style={{
                            padding: '8px 16px',
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            cursor: cleanupDeleting ? 'not-allowed' : 'pointer',
                            fontWeight: 500,
                            fontSize: 14,
                            color: '#6b7280',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { if (!cleanupDeleting) { e.target.style.background = '#e5e7eb'; } }}
                          onMouseLeave={(e) => { if (!cleanupDeleting) { e.target.style.background = '#f3f4f6'; } }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCMS;
